import React, { useEffect, useMemo, useState } from 'react';
import { Form, Input, message } from 'antd';
import { Button, FormItem } from 'components/elements';
import {
  namedOperations,
  useAddPersonalEventMutation,
  useGetMyPersonalEventsQuery,
  useUpdatePersonalEventMutation,
} from 'graphql/types';
import {
  PersonalEventDateChip,
  PersonalEventDateList,
  PersonalEventDatePickerHint,
  PersonalEventFormActions,
  PersonalEventFormFields,
  PersonalEventSelectedDate,
} from 'containers/calendarView/index.styled';
import {
  dateKeysToEventDays,
  formatDayKey,
  getPersonalEventDates,
  momentToDateKey,
  stripHtml,
} from './personalEventFormUtils';
import type { Moment } from 'moment';

type FormValues = {
  subject: string;
  color: string;
  description: string;
};

type PersonalEventFormPanelProps = {
  selectedDate: Moment;
  editId?: string | null;
  onCancel: () => void;
  onSaved: () => void;
  onDateListChange?: (dates: number[]) => void;
};

export default function PersonalEventFormPanel({
  selectedDate,
  editId,
  onCancel,
  onSaved,
  onDateListChange,
}: PersonalEventFormPanelProps) {
  const isEdit = Boolean(editId);
  const [form] = Form.useForm<FormValues>();
  const [dateList, setDateList] = useState<number[]>([]);

  const { data: personalEventsData, loading: loadingEvents } = useGetMyPersonalEventsQuery({
    skip: !isEdit,
  });

  const existingEvent = useMemo(() => {
    if (!isEdit || !editId) return null;
    return personalEventsData?.myPersonalEvents?.find((event) => event?.id === editId) ?? null;
  }, [editId, isEdit, personalEventsData]);

  const [createPersonalEvent, { loading: creating }] = useAddPersonalEventMutation({
    refetchQueries: [namedOperations.Query.GetMyPersonalEvents],
  });
  const [updatePersonalEvent, { loading: updating }] = useUpdatePersonalEventMutation({
    refetchQueries: [namedOperations.Query.GetMyPersonalEvents],
  });

  const saving = creating || updating;
  const sortedDateList = useMemo(() => [...dateList].sort((a, b) => a - b), [dateList]);

  useEffect(() => {
    onDateListChange?.(dateList);
  }, [dateList, onDateListChange]);

  useEffect(() => {
    if (!isEdit) {
      form.resetFields();
      form.setFieldsValue({ color: '#1a2744' });
      setDateList([]);
      return;
    }
    if (!existingEvent) return;
    form.setFieldsValue({
      subject: existingEvent.subject ?? '',
      color: existingEvent.color ?? '#1a2744',
      description: existingEvent.description ? stripHtml(existingEvent.description) : '',
    });
    setDateList(getPersonalEventDates(existingEvent));
  }, [existingEvent, form, isEdit]);

  const handleAddSelectedDate = () => {
    const dayKey = momentToDateKey(selectedDate);
    if (dateList.includes(dayKey)) {
      message.info('This date is already in the list.');
      return;
    }
    setDateList((prev) => [...prev, dayKey]);
    message.success('Date added.');
  };

  const handleRemoveDate = (dayKey: number) => {
    setDateList((prev) => prev.filter((date) => date !== dayKey));
  };

  const onSubmit = async (values: FormValues) => {
    if (!dateList.length) {
      message.error('Select a date on the calendar and click Add date.');
      return;
    }
    try {
      if (isEdit && editId) {
        await updatePersonalEvent({
          variables: {
            input: {
              personalEventId: editId,
              subject: values.subject.trim(),
              color: values.color,
              description: values.description.trim(),
              eventDays: dateKeysToEventDays(dateList),
            },
          },
        });
        message.success('Personal event updated.');
      } else {
        await createPersonalEvent({
          variables: {
            input: {
              subject: values.subject.trim(),
              color: values.color,
              description: values.description.trim(),
              eventDays: dateKeysToEventDays(dateList),
            },
          },
        });
        message.success('Personal event added.');
      }
      onSaved();
    } catch (err) {
      if (err instanceof Error) {
        message.error(err.message);
      }
    }
  };

  if (isEdit && loadingEvents) {
    return <PersonalEventDatePickerHint>Loading personal event...</PersonalEventDatePickerHint>;
  }

  if (isEdit && !loadingEvents && !existingEvent) {
    return (
      <>
        <PersonalEventDatePickerHint>Personal event not found.</PersonalEventDatePickerHint>
        <PersonalEventFormActions>
          <Button type="default" onClick={onCancel}>
            Back
          </Button>
        </PersonalEventFormActions>
      </>
    );
  }

  return (
    <Form form={form} layout="vertical" onFinish={onSubmit}>
      <PersonalEventFormFields>
        <FormItem
          label="Subject"
          name="subject"
          theme="primary"
          rules={[{ required: true, message: 'Please enter subject' }]}
        >
          <Input placeholder="Enter event subject" />
        </FormItem>

        <Form.Item label="Dates" required>
          <PersonalEventDatePickerHint>
            Click a day on the calendar, then add it to this event.
          </PersonalEventDatePickerHint>
          <PersonalEventSelectedDate>
            Selected: {selectedDate.format('YYYY.MM.DD')}
          </PersonalEventSelectedDate>
          <Button type="dashed" onClick={handleAddSelectedDate} style={{ marginTop: 8 }}>
            Add date
          </Button>
          <PersonalEventDateList>
            {sortedDateList.length ? (
              sortedDateList.map((dayKey) => (
                <PersonalEventDateChip key={dayKey} type="button" onClick={() => handleRemoveDate(dayKey)}>
                  {formatDayKey(dayKey)} ×
                </PersonalEventDateChip>
              ))
            ) : (
              <PersonalEventDatePickerHint>No dates added yet.</PersonalEventDatePickerHint>
            )}
          </PersonalEventDateList>
        </Form.Item>

        <FormItem
          label="Color"
          name="color"
          theme="primary"
          initialValue="#1a2744"
          rules={[{ required: true, message: 'Please choose a color' }]}
        >
          <Input type="color" style={{ width: 72, height: 40, padding: 4, cursor: 'pointer' }} />
        </FormItem>

        <FormItem
          label="Description"
          name="description"
          theme="primary"
          rules={[{ required: true, message: 'Please enter description' }]}
        >
          <Input.TextArea rows={4} placeholder="Write a short description" />
        </FormItem>
      </PersonalEventFormFields>

      <PersonalEventFormActions>
        <Button type="primary" onClick={() => form.submit()} loading={saving}>
          {isEdit ? 'Save changes' : 'Save Personal Event'}
        </Button>
        <Button type="default" onClick={onCancel}>
          Cancel
        </Button>
      </PersonalEventFormActions>
    </Form>
  );
}
